import React from "react"
import { Send } from "lucide-react"
import { useDevice } from "@shared/hooks"
import type { UIText } from "../types"

interface ChatInputProps {
  messageText: string
  setMessageText: (text: string) => void
  onSendMessage: () => Promise<void>
  isSendingMessage: boolean
  isCreatingChat: boolean
  uiText: UIText
}

const ChatInputComponent: React.FC<ChatInputProps> = ({
  messageText,
  setMessageText,
  onSendMessage,
  isSendingMessage,
  isCreatingChat,
  uiText,
}) => {
  const { isMobile } = useDevice()

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSendingMessage && !isCreatingChat && messageText.trim()) {
      await onSendMessage()
    }
  }

  const handleSend = async () => {
    if (!messageText.trim() || isSendingMessage || isCreatingChat) return
    await onSendMessage()
  }

  return (
    // Field and send button are two controls side by side, not one joined pill: the button is the
    // primary action and carries the accent (docs/design/AI Assistant.dc.html).
    <div
      className={`shrink-0 border-t border-border px-5 pt-3 pb-4 max-md:px-3 max-md:pt-2.5 max-md:pb-3.5 ${
        isMobile ? "pb-safe-bottom" : ""
      }`}
    >
      <span
        id="chat-input-description"
        className="sr-only"
      >
        Press Enter to send your message
      </span>
      <div className="flex items-end gap-2">
        <input
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder={uiText.typePlaceholder}
          className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm shadow-inputShadow transition-colors focus:border-primary focus:ring-1 focus:ring-ring focus:outline-none disabled:opacity-60 max-md:h-11"
          onKeyDown={handleKeyDown}
          aria-label={uiText.typePlaceholder}
          aria-describedby="chat-input-description"
          disabled={isSendingMessage || isCreatingChat}
        />
        <button
          onClick={handleSend}
          disabled={isSendingMessage || isCreatingChat || !messageText.trim()}
          className="flex size-10 flex-none cursor-pointer items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary-700 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 max-md:size-11"
          aria-label="Send message"
        >
          <Send
            size={16}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  )
}

export const ChatInput = React.memo(ChatInputComponent)
